import {
  VerificationConnectAction,
  VerificationConnectRequest,
} from '../../_types/flow-form-verification'

/**
 * Reads the popup's instructions off the URL.
 *
 * QuickPass mounts a HashRouter, so the query lives after the hash and
 * `window.location.search` is empty here. Reading it off the hash directly is
 * the only thing that works, and getting this wrong presents as a popup that
 * opens, looks fine, and silently has no idea what it was asked to do.
 *
 * @param {string} hash - the full location hash
 * @returns {URLSearchParams} the parameters, empty when there are none
 */
const readHashParameters = (hash: string): URLSearchParams => {
  const separatorIndex = hash.indexOf('?')

  if (separatorIndex === -1) {
    return new URLSearchParams()
  }

  return new URLSearchParams(hash.slice(separatorIndex + 1))
}

/**
 * Reads which of the two jobs this popup was opened to do.
 *
 * @param {URLSearchParams} parameters - the popup's parameters
 * @returns {VerificationConnectAction} the action, defaulting to sharing
 */
const readAction = (
  parameters: URLSearchParams,
): VerificationConnectAction => {
  const action = parameters.get('action')

  if (action === 'save') {
    return 'save'
  }

  if (action === 'connect') {
    return 'connect'
  }

  return 'share'
}

/**
 * Reads the form step this popup is acting on.
 *
 * @param {URLSearchParams} parameters - the popup's parameters
 * @returns {VerificationConnectRequest} the step, with empty strings for anything missing
 */
const readRequest = (
  parameters: URLSearchParams,
): VerificationConnectRequest => ({
  companyId: parameters.get('company') ?? '',
  formId: parameters.get('form') ?? '',
  nodeId: parameters.get('node') ?? '',
  submissionId: parameters.get('submission') ?? '',
  verificationRequestToken: parameters.get('request') ?? '',
})

/**
 * Reports whether the popup has everything it needs to act.
 *
 * The request token is deliberately not required. Most people reach a verify
 * step by filling a form in rather than by answering an emailed ask, and
 * refusing to work without one would break the ordinary case to serve the rare
 * one.
 *
 * @param {VerificationConnectRequest} request - the step read off the URL
 * @returns {boolean} true when every field the popup cannot work without is present
 */
const isRequestComplete = (request: VerificationConnectRequest): boolean =>
  request.companyId !== '' &&
  request.formId !== '' &&
  request.nodeId !== '' &&
  request.submissionId !== ''

export { readHashParameters, readAction, readRequest, isRequestComplete }
